import { lazy, Suspense, useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react'
import {
  ChevronLeft,
  Download,
  HardDrive,
  HelpCircle,
  Layers3,
  Redo2,
  Save,
  ShieldAlert,
  Undo2,
} from 'lucide-react'
import { detectBrowserCompatibility } from './browser-compatibility'
import type { HelpTopicId } from './help-content'
import {
  clearGuidedTourSession,
  createGuidedTourProject,
  GUIDED_TOUR_STEPS,
  loadGuidedTourSession,
  newGuidedTourSession,
  saveGuidedTourSession,
  type GuidedTourSession,
} from './guided-tour'
import {
  createRecoverySnapshot,
  countRecoverySnapshots,
  deleteRecoverySnapshot,
  dismissPendingSave,
  getRawProjectRecord,
  listRecoverySnapshots,
  loadProjectLibrary,
  removeDamagedProjectRecord,
  removeProject,
  restoreRecoveryProject,
  saveProject,
  type DamagedProjectSummary,
} from './db'
import { exportProjectFile, importProjectFile } from './project-file'
import { applyPwaUpdate, getPwaState, promptDesktopPwaInstall, subscribePwa } from './pwa'
import { isSavedRevisionCurrent, type RecoveryCandidate, type RecoveryReason, type RecoverySnapshot } from './recovery'
import { readLocalStorageStatus, requestPersistentStorage, type LocalStorageStatus } from './storage'
import { useEditorStore } from './store'
import type { DeckProject, WorkspaceTab } from './types'
import { downloadBlob, isEditableTarget, toFileSlug } from './utils'
import { ProjectDeleteDialog } from './components/ProjectDeleteDialog'
import { AppErrorBoundary } from './components/AppErrorBoundary'
import { BrowserCompatibilityScreen, BrowserStorageFailureScreen, CompatibilityDialog } from './components/CompatibilityDialog'
import { ProjectLibrary } from './components/ProjectLibrary'
import { ProjectImportErrorDialog } from './components/ProjectImportErrorDialog'
import { RecoveryDialog } from './components/RecoveryDialog'
import { StorageRecoveryDialog } from './components/StorageRecoveryDialog'
import { GuidedTour } from './components/GuidedTour'
import { GuidedTourCompleteDialog } from './components/GuidedTourCompleteDialog'
import { HelpDialog } from './components/HelpDialog'
import { DesktopRequiredScreen, InstallDialog, PwaUpdateNotice } from './components/PwaUi'

const DesignWorkspace = lazy(() => import('./components/DesignWorkspace').then((module) => ({ default: module.DesignWorkspace })))
const DataWorkspace = lazy(() => import('./components/DataWorkspace').then((module) => ({ default: module.DataWorkspace })))
const RenderWorkspace = lazy(() => import('./components/RenderWorkspace').then((module) => ({ default: module.RenderWorkspace })))

const tabs: { id: WorkspaceTab; label: string; detail: string; step: number }[] = [
  { id: 'design', label: 'Design', detail: 'Build template', step: 1 },
  { id: 'data', label: 'Data', detail: 'Fill cards', step: 2 },
  { id: 'render', label: 'Render', detail: 'Export files', step: 3 },
]

interface ToastState {
  message: string
  tone: 'success' | 'error'
}

interface PersistenceNotice {
  tone: 'success' | 'warning'
  text: string
}

type SaveState = 'saved' | 'saving' | 'failed'

const unknownStorageStatus: LocalStorageStatus = { pressure: 'unknown' }

function App() {
  const {
    project,
    activeTab,
    selectedElementIds,
    dirty,
    past,
    future,
    setProject,
    setActiveTab,
    markSaved,
    renameProject,
    deleteSelected,
    duplicateSelected,
    copySelected,
    pasteClipboard,
    nudgeSelected,
    groupSelected,
    ungroupSelected,
    undo,
    redo,
  } = useEditorStore()
  const [projects, setProjects] = useState<DeckProject[]>([])
  const [loading, setLoading] = useState(true)
  const [storageStartupError, setStorageStartupError] = useState<Error | null>(null)
  const [toast, setToast] = useState<ToastState | null>(null)
  const [compatibility] = useState(detectBrowserCompatibility)
  const [compatibilityOpen, setCompatibilityOpen] = useState(false)
  const [projectImportError, setProjectImportError] = useState<Error | null>(null)
  const [saveState, setSaveState] = useState<SaveState>('saved')
  const [recoveryCandidates, setRecoveryCandidates] = useState<RecoveryCandidate[]>([])
  const [recoverySnapshots, setRecoverySnapshots] = useState<RecoverySnapshot[]>([])
  const [recoveryCount, setRecoveryCount] = useState(0)
  const [recoveryLoading, setRecoveryLoading] = useState(false)
  const [damagedProjects, setDamagedProjects] = useState<DamagedProjectSummary[]>([])
  const [storageStatus, setStorageStatus] = useState<LocalStorageStatus>(unknownStorageStatus)
  const [persistenceNotice, setPersistenceNotice] = useState<PersistenceNotice | null>(null)
  const [storageOpen, setStorageOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<DeckProject | null>(null)
  const [dialogBusy, setDialogBusy] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)
  const [helpTopic, setHelpTopic] = useState<HelpTopicId>('getting-started')
  const [tourSession, setTourSession] = useState<GuidedTourSession | null>(() => loadGuidedTourSession())
  const [tourActive, setTourActive] = useState(false)
  const [tourCompleteOpen, setTourCompleteOpen] = useState(false)
  const [tourCompleteBusy, setTourCompleteBusy] = useState(false)
  const [installOpen, setInstallOpen] = useState(false)
  const pwa = useSyncExternalStore(subscribePwa, getPwaState, getPwaState)
  const saveQueue = useRef<Promise<void>>(Promise.resolve())

  const openHelp = useCallback((topic: HelpTopicId = 'getting-started') => {
    setTourActive(false)
    setHelpTopic(topic)
    setHelpOpen(true)
  }, [])
  const closeHelp = useCallback(() => setHelpOpen(false), [])

  const message = useCallback((text: string, tone: ToastState['tone'] = 'success') => {
    setToast({ message: text, tone })
    window.setTimeout(() => setToast(null), 3_500)
  }, [])

  const refreshLibrary = useCallback(async () => {
    const state = await loadProjectLibrary()
    setProjects(state.projects)
    setRecoveryCandidates(state.recoveryCandidates)
    setDamagedProjects(state.damagedProjects)
  }, [])
  const refreshRecoverySnapshots = useCallback(async () => setRecoverySnapshots(await listRecoverySnapshots()), [])
  const refreshRecoveryCount = useCallback(async () => setRecoveryCount(await countRecoverySnapshots()), [])
  const refreshStorage = useCallback(async () => {
    try {
      setStorageStatus(await readLocalStorageStatus())
    } catch {
      setStorageStatus(unknownStorageStatus)
    }
  }, [])
  const refreshReliability = useCallback(async () => {
    await Promise.all([refreshLibrary(), refreshRecoveryCount(), refreshStorage()])
  }, [refreshLibrary, refreshRecoveryCount, refreshStorage])

  const enqueueSave = useCallback((target: DeckProject, reason: RecoveryReason) => {
    const task = saveQueue.current.catch(() => undefined).then(() => saveProject(target, reason))
    saveQueue.current = task.then(() => undefined, () => undefined)
    return task
  }, [])

  const persistProject = useCallback(async (target: DeckProject, reason: RecoveryReason = 'autosave', announce = false) => {
    const savedUpdatedAt = target.updatedAt
    setSaveState('saving')
    try {
      const result = await enqueueSave(target, reason)
      const current = useEditorStore.getState().project
      if (current?.id === target.id && isSavedRevisionCurrent(current, savedUpdatedAt)) {
        markSaved()
        setSaveState('saved')
      } else if (current?.id !== target.id) {
        setSaveState('saved')
      }
      setProjects((current) => [target, ...current.filter((candidate) => candidate.id !== target.id)]
        .sort((left, right) => Date.parse(right.updatedAt) - Date.parse(left.updatedAt)))
      if (result.snapshotStored) void refreshRecoveryCount()
      void refreshStorage()
      if (announce) message('Project saved.')
      return true
    } catch {
      setSaveState('failed')
      message('Local save failed. Your work is still open. Export a project file before closing.', 'error')
      void refreshStorage()
      return false
    }
  }, [enqueueSave, markSaved, message, refreshRecoveryCount, refreshStorage])

  useEffect(() => {
    if (!compatibility.supported) {
      setLoading(false)
      return
    }
    refreshReliability()
      .catch((error) => setStorageStartupError(error instanceof Error ? error : new Error('The local project database could not be opened.')))
      .finally(() => setLoading(false))
  }, [compatibility.supported, refreshReliability])

  useEffect(() => {
    if (loading || !tourSession || project?.id === tourSession.projectId) return
    if (projects.some((candidate) => candidate.id === tourSession.projectId)) return
    clearGuidedTourSession()
    setTourSession(null)
    setTourActive(false)
  }, [loading, project?.id, projects, tourSession])

  useEffect(() => {
    if (!project || !dirty) return
    const timer = window.setTimeout(() => void persistProject(project), 650)
    return () => window.clearTimeout(timer)
  }, [dirty, persistProject, project])

  useEffect(() => {
    const saveWhenHidden = () => {
      if (document.visibilityState !== 'hidden') return
      const state = useEditorStore.getState()
      if (state.project && state.dirty) void persistProject(state.project, 'visibility')
    }
    document.addEventListener('visibilitychange', saveWhenHidden)
    return () => document.removeEventListener('visibilitychange', saveWhenHidden)
  }, [persistProject])

  useEffect(() => {
    if (!project) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (helpOpen || tourCompleteOpen) return
      const command = event.ctrlKey || event.metaKey
      if (command && event.key.toLowerCase() === 's') {
        event.preventDefault()
        const current = useEditorStore.getState().project
        if (current) void persistProject(current, 'manual', true)
        return
      }
      if (isEditableTarget(event.target)) return
      if (command && event.key.toLowerCase() === 'z') {
        event.preventDefault()
        event.shiftKey ? redo() : undo()
      }
      if (command && event.key.toLowerCase() === 'y') {
        event.preventDefault()
        redo()
      }
      if ((event.key === 'Delete' || event.key === 'Backspace') && selectedElementIds.length) {
        event.preventDefault()
        deleteSelected()
      }
      if (command && event.key.toLowerCase() === 'c' && selectedElementIds.length) {
        event.preventDefault()
        copySelected()
      }
      if (command && event.key.toLowerCase() === 'v') {
        event.preventDefault()
        pasteClipboard()
      }
      if (command && event.key.toLowerCase() === 'd' && selectedElementIds.length) {
        event.preventDefault()
        duplicateSelected()
      }
      if (command && event.key.toLowerCase() === 'g') {
        event.preventDefault()
        event.shiftKey ? ungroupSelected() : groupSelected()
      }
      if (selectedElementIds.length && ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
        event.preventDefault()
        const amount = event.shiftKey ? 1 : 0.25
        nudgeSelected(event.key === 'ArrowLeft' ? -amount : event.key === 'ArrowRight' ? amount : 0, event.key === 'ArrowUp' ? -amount : event.key === 'ArrowDown' ? amount : 0)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [copySelected, deleteSelected, duplicateSelected, groupSelected, helpOpen, nudgeSelected, pasteClipboard, persistProject, project, redo, selectedElementIds, tourCompleteOpen, undo, ungroupSelected])

  const open = (nextProject: DeckProject) => setProject(nextProject)
  const create = async (nextProject: DeckProject) => {
    open(nextProject)
    const saved = await persistProject(nextProject)
    return saved
  }
  const startOrResumeTour = async () => {
    if (!pwa.desktopSupported) {
      message('The guided tour requires a desktop-sized screen.', 'error')
      return
    }
    setHelpOpen(false)
    const current = useEditorStore.getState()
    if (current.project && current.dirty && !await persistProject(current.project, 'manual')) return

    const savedSession = tourSession
    const savedProject = savedSession
      ? current.project?.id === savedSession.projectId
        ? current.project
        : projects.find((candidate) => candidate.id === savedSession.projectId)
      : null

    if (savedSession && savedProject) {
      if (current.project?.id !== savedProject.id) open(savedProject)
      setTourActive(true)
      return
    }

    if (savedSession) clearGuidedTourSession()
    const tutorialProject = createGuidedTourProject()
    if (!await create(tutorialProject)) return
    const session = newGuidedTourSession(tutorialProject.id)
    saveGuidedTourSession(session)
    setTourSession(session)
    setTourCompleteOpen(false)
    setTourActive(true)
  }
  const updateTourStep = (stepIndex: number) => {
    if (!tourSession) return
    const nextIndex = Math.min(GUIDED_TOUR_STEPS.length - 1, Math.max(0, stepIndex))
    const session = newGuidedTourSession(tourSession.projectId, nextIndex)
    saveGuidedTourSession(session)
    setTourSession(session)
  }
  const restartTour = async () => {
    if (!tourSession) return
    const tutorialProject = createGuidedTourProject(tourSession.projectId)
    open(tutorialProject)
    const session = newGuidedTourSession(tutorialProject.id)
    saveGuidedTourSession(session)
    setTourSession(session)
    setTourActive(true)
    setTourCompleteOpen(false)
    if (!await persistProject(tutorialProject, 'manual')) setTourActive(false)
  }
  const finishTourKeepProject = () => {
    clearGuidedTourSession()
    setTourSession(null)
    setTourCompleteOpen(false)
    setTourActive(false)
    message('Guided tour complete. The practice project is yours to keep.')
  }
  const finishTourDeleteProject = async () => {
    if (!tourSession) return
    setTourCompleteBusy(true)
    try {
      const current = useEditorStore.getState()
      if (current.project?.id === tourSession.projectId && current.dirty && !await persistProject(current.project, 'manual')) return
      await saveQueue.current
      await removeProject(tourSession.projectId)
      clearGuidedTourSession()
      setTourSession(null)
      setTourCompleteOpen(false)
      setTourActive(false)
      if (useEditorStore.getState().project?.id === tourSession.projectId) setProject(null)
      await refreshReliability()
      message('Practice project deleted.')
    } catch {
      message('Unable to delete the practice project.', 'error')
    } finally {
      setTourCompleteBusy(false)
    }
  }
  const importFile = async (file: File) => {
    try {
      if (await create(await importProjectFile(file))) message('Project imported.')
    } catch (error) {
      setProjectImportError(error instanceof Error ? error : new Error('Unable to import project.'))
    }
  }
  const remove = async (target: DeckProject) => {
    setDeleteTarget(target)
  }
  const goHome = async () => {
    if (project && dirty && !await persistProject(project, 'manual')) return
    setProject(null)
    setTourActive(false)
    setSaveState('saved')
    await refreshReliability()
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return
    setDialogBusy(true)
    try {
      await removeProject(deleteTarget.id)
      if (tourSession?.projectId === deleteTarget.id) {
        clearGuidedTourSession()
        setTourSession(null)
        setTourActive(false)
      }
      setDeleteTarget(null)
      await refreshReliability()
      message('Project deleted.')
    } catch {
      message('Unable to delete this project.', 'error')
    } finally {
      setDialogBusy(false)
    }
  }

  const currentRecoveryCandidate = recoveryCandidates[0]
  const recoverPending = async () => {
    if (!currentRecoveryCandidate) return
    setDialogBusy(true)
    try {
      const restored = await restoreRecoveryProject(currentRecoveryCandidate.pending.project)
      await refreshReliability()
      open(restored)
      message('Unsaved work recovered.')
    } catch {
      message('Deckelier could not restore this recovery copy.', 'error')
    } finally {
      setDialogBusy(false)
    }
  }
  const useLastSaved = async () => {
    if (!currentRecoveryCandidate) return
    setDialogBusy(true)
    try {
      const { pending, savedProject, savedProjectDamaged } = currentRecoveryCandidate
      if (savedProjectDamaged) await removeProject(pending.projectId)
      else await dismissPendingSave(pending.projectId)
      await refreshReliability()
      if (savedProject && !savedProjectDamaged) open(savedProject)
    } catch {
      message('Deckelier could not dismiss this recovery copy.', 'error')
    } finally {
      setDialogBusy(false)
    }
  }

  const createSnapshot = async () => {
    if (!project) return
    setDialogBusy(true)
    try {
      await createRecoverySnapshot(project, 'manual', true)
      await Promise.all([refreshRecoverySnapshots(), refreshRecoveryCount(), refreshStorage()])
      message('Recovery copy saved.')
    } catch {
      message('Unable to create a recovery copy.', 'error')
    } finally {
      setDialogBusy(false)
    }
  }
  const restoreSnapshot = async (snapshot: RecoverySnapshot) => {
    setDialogBusy(true)
    try {
      const restored = await restoreRecoveryProject(snapshot.project)
      await refreshReliability()
      setStorageOpen(false)
      setRecoverySnapshots([])
      open(restored)
      message('Recovery copy restored.')
    } catch {
      message('Unable to restore this recovery copy.', 'error')
    } finally {
      setDialogBusy(false)
    }
  }
  const removeSnapshot = async (snapshot: RecoverySnapshot) => {
    setDialogBusy(true)
    try {
      await deleteRecoverySnapshot(snapshot.id)
      await Promise.all([refreshRecoverySnapshots(), refreshRecoveryCount(), refreshStorage()])
      message('Recovery copy deleted.')
    } catch {
      message('Unable to delete this recovery copy.', 'error')
    } finally {
      setDialogBusy(false)
    }
  }
  const protectStorage = async () => {
    setDialogBusy(true)
    setPersistenceNotice(null)
    try {
      const result = await requestPersistentStorage()
      await refreshStorage()
      if (result === 'granted' || result === 'already-granted') {
        setPersistenceNotice({ tone: 'success', text: result === 'already-granted' ? 'Storage protection was already enabled for Deckelier.' : 'The browser granted storage protection for Deckelier.' })
        message('Browser storage protection is on.')
      } else if (result === 'denied') {
        setPersistenceNotice({ tone: 'warning', text: 'The browser declined this request silently. Chromium-based browsers decide automatically and usually show no permission prompt. Your projects still save locally, but exported .deckelier files remain the reliable backup.' })
      } else {
        setPersistenceNotice({ tone: 'warning', text: 'This browser does not offer a storage-protection request. Your projects still save locally, but export important work as .deckelier files.' })
      }
    } catch {
      setPersistenceNotice({ tone: 'warning', text: 'The browser could not complete the storage-protection request. Your current projects were not changed.' })
      message('Unable to request browser storage protection.', 'error')
    } finally {
      setDialogBusy(false)
    }
  }
  const exportRecoverySnapshot = (snapshot: RecoverySnapshot) => {
    exportProjectFile(snapshot.project).catch(() => message('Unable to export this recovery copy.', 'error'))
  }
  const exportDamagedProject = async (damaged: DamagedProjectSummary) => {
    try {
      const rawProject = await getRawProjectRecord(damaged.id)
      if (rawProject === undefined) throw new Error('The project record no longer exists.')
      const contents = JSON.stringify(rawProject, null, 2)
      downloadBlob(new Blob([contents], { type: 'application/json' }), `${toFileSlug(damaged.name)}-damaged-project.json`)
      message('Unreadable project record downloaded.')
    } catch {
      message('Unable to download this unreadable project record.', 'error')
    }
  }
  const deleteDamagedProject = async (damaged: DamagedProjectSummary) => {
    setDialogBusy(true)
    try {
      await removeDamagedProjectRecord(damaged.id)
      await refreshReliability()
      message('Unreadable project record removed. Recovery copies were kept.')
    } catch {
      message('Unable to remove this unreadable project record.', 'error')
    } finally {
      setDialogBusy(false)
    }
  }
  const openStorage = async () => {
    setStorageOpen(true)
    setRecoveryLoading(true)
    try {
      await refreshRecoverySnapshots()
    } catch {
      message('Unable to open recovery history.', 'error')
    } finally {
      setRecoveryLoading(false)
    }
  }
  const closeStorage = () => {
    if (dialogBusy || recoveryLoading) return
    setStorageOpen(false)
    setRecoverySnapshots([])
  }
  const installDesktopApp = async () => {
    const outcome = await promptDesktopPwaInstall()
    if (outcome === 'accepted') message('Deckelier installed.')
  }
  const updateDesktopApp = async () => {
    const current = useEditorStore.getState()
    if (current.project && current.dirty && !await persistProject(current.project, 'manual')) return
    await saveQueue.current
    if (!applyPwaUpdate()) message('The update is not ready yet.', 'error')
  }

  if (!compatibility.supported) return <BrowserCompatibilityScreen report={compatibility} />

  if (loading) {
    return <div className="app-loading"><div className="brand-mark"><Layers3 /></div><strong>Opening Deckelier…</strong></div>
  }

  if (storageStartupError) return <BrowserStorageFailureScreen error={storageStartupError} />

  const damagedProjectCount = damagedProjects.length

  if (!project) {
    return (
      <>
        <ProjectLibrary
          projects={projects}
          onCreate={create}
          onOpen={open}
          onImport={importFile}
          onDelete={remove}
          onOpenStorage={() => void openStorage()}
          recoveryCount={recoveryCount}
          damagedProjectCount={damagedProjectCount}
          storageStatus={storageStatus}
          compatibilityWarningCount={compatibility.warnings.length}
          onOpenCompatibility={() => setCompatibilityOpen(true)}
          onOpenHelp={() => openHelp()}
          onStartTour={() => void startOrResumeTour()}
          canResumeTour={Boolean(tourSession)}
          editorSupported={pwa.desktopSupported}
          showInstall={pwa.desktopSupported && !pwa.installed}
          onOpenInstall={() => setInstallOpen(true)}
        />
        <HelpDialog open={helpOpen} initialTopic={helpTopic} canResumeTour={Boolean(tourSession)} onClose={closeHelp} onStartTour={() => void startOrResumeTour()} />
        <InstallDialog open={installOpen} nativeInstallAvailable={pwa.installAvailable} onClose={() => setInstallOpen(false)} onInstall={installDesktopApp} />
        {currentRecoveryCandidate && <RecoveryDialog candidate={currentRecoveryCandidate} busy={dialogBusy} onRecover={recoverPending} onUseSaved={useLastSaved} onExport={() => exportProjectFile(currentRecoveryCandidate.pending.project).catch(() => message('Unable to export this recovery copy.', 'error'))} />}
        {storageOpen && <StorageRecoveryDialog project={null} snapshots={recoverySnapshots} snapshotsLoading={recoveryLoading} status={storageStatus} persistenceNotice={persistenceNotice} damagedProjects={damagedProjects} busy={dialogBusy || recoveryLoading} onClose={closeStorage} onCreateSnapshot={createSnapshot} onRestore={restoreSnapshot} onDelete={removeSnapshot} onExport={exportRecoverySnapshot} onDownloadDamaged={exportDamagedProject} onDeleteDamaged={deleteDamagedProject} onRequestPersistence={protectStorage} />}
        {deleteTarget && <ProjectDeleteDialog project={deleteTarget} busy={dialogBusy} onClose={() => setDeleteTarget(null)} onConfirm={confirmDelete} />}
        {projectImportError && <ProjectImportErrorDialog error={projectImportError} onClose={() => setProjectImportError(null)} />}
        {compatibilityOpen && <CompatibilityDialog report={compatibility} onClose={() => setCompatibilityOpen(false)} />}
        <PwaUpdateNotice available={pwa.updateAvailable} onUpdate={() => void updateDesktopApp()} />
        {toast && <div className={`toast ${toast.tone}`}>{toast.message}</div>}
      </>
    )
  }

  if (!pwa.desktopSupported) {
    return (
      <>
        <DesktopRequiredScreen
          projectName={project.name}
          onBack={() => void goHome()}
          onBackup={() => exportProjectFile(project).catch(() => message('Unable to export project.', 'error'))}
          onHelp={() => openHelp('getting-started')}
        />
        <HelpDialog open={helpOpen} initialTopic={helpTopic} canResumeTour={Boolean(tourSession)} onClose={closeHelp} onStartTour={() => void startOrResumeTour()} />
        <PwaUpdateNotice available={pwa.updateAvailable} onUpdate={() => void updateDesktopApp()} />
        {toast && <div className={`toast ${toast.tone}`}>{toast.message}</div>}
      </>
    )
  }

  return (
    <div className="editor-app">
      <header className="editor-header">
        <div className="editor-header-left">
          <div className="editor-brand">
            <button className="icon-button back-button" aria-label="Back to projects" onClick={goHome}><ChevronLeft size={18} /></button>
            <div className="mini-brand-mark"><Layers3 size={18} /></div>
            <strong>Deckelier</strong>
            <span className="beta-badge">Beta</span>
          </div>
          <div className="project-title-control">
            <input value={project.name} onChange={(event) => renameProject(event.target.value)} aria-label="Project name" />
            <span className={saveState === 'failed' ? 'save-failed' : ''}>{saveState === 'failed' ? 'Save failed' : dirty || saveState === 'saving' ? 'Saving…' : 'Saved locally'}</span>
          </div>
        </div>
        <nav className="workspace-tabs" aria-label="Project workspace" data-tour-target="workspace-tabs">
          {tabs.map(({ id, label, detail, step }) => (
            <button key={id} data-tour-target={`tab-${id}`} className={activeTab === id ? 'active' : ''} aria-current={activeTab === id ? 'step' : undefined} onClick={() => setActiveTab(id)}>
              <span className="tab-step">{step}</span><span className="tab-copy"><strong>{label}</strong><small>{detail}</small></span>
            </button>
          ))}
        </nav>
        <div className="header-actions">
          <button className="icon-button" aria-label="Undo" disabled={!past.length} onClick={undo}><Undo2 size={17} /></button>
          <button className="icon-button" aria-label="Redo" disabled={!future.length} onClick={redo}><Redo2 size={17} /></button>
          <span className="header-divider" />
          <button className="icon-button" aria-label="Help and guides" title="Help and guides" onClick={() => openHelp()}><HelpCircle size={17} /></button>
          {!pwa.installed && <button className="icon-button" aria-label="Install Deckelier" title="Install Deckelier" onClick={() => setInstallOpen(true)}><Download size={17} /></button>}
          {compatibility.warnings.length > 0 && <button className="icon-button compatibility-status-button attention" aria-label="Browser compatibility warnings" title="Browser compatibility warnings" onClick={() => setCompatibilityOpen(true)}><ShieldAlert size={17} /></button>}
          <button className={`icon-button storage-status-button ${storageStatus.pressure === 'warning' || storageStatus.pressure === 'critical' || damagedProjectCount > 0 ? 'attention' : ''}`} aria-label="Storage and recovery" title={storageStatus.pressure === 'warning' || storageStatus.pressure === 'critical' ? 'Local storage needs attention' : 'Storage and recovery'} onClick={() => void openStorage()}><HardDrive size={17} /></button>
          <button className="button secondary compact" onClick={() => exportProjectFile(project).catch(() => message('Unable to export project.', 'error'))}><Save size={15} /> Save backup</button>
        </div>
      </header>
      <main className="editor-main">
        <AppErrorBoundary key={`${project.id}:${activeTab}`} scope="workspace" getProject={() => useEditorStore.getState().project} onExit={() => void goHome()}>
          <Suspense fallback={<div className="workspace-loading">Preparing workspace…</div>}>
            {activeTab === 'design' && <DesignWorkspace onOpenHelp={openHelp} />}
            {activeTab === 'data' && <DataWorkspace onOpenHelp={openHelp} />}
            {activeTab === 'render' && <RenderWorkspace onMessage={message} onOpenHelp={openHelp} />}
          </Suspense>
        </AppErrorBoundary>
      </main>
      <HelpDialog open={helpOpen} initialTopic={helpTopic} canResumeTour={Boolean(tourSession)} onClose={closeHelp} onStartTour={() => void startOrResumeTour()} />
      <InstallDialog open={installOpen} nativeInstallAvailable={pwa.installAvailable} onClose={() => setInstallOpen(false)} onInstall={installDesktopApp} />
      {tourActive && tourSession && project.id === tourSession.projectId && (
        <GuidedTour
          session={tourSession}
          project={project}
          activeTab={activeTab}
          onStepChange={updateTourStep}
          onExit={() => setTourActive(false)}
          onRestart={() => void restartTour()}
          onComplete={() => { setTourActive(false); setTourCompleteOpen(true) }}
          onRequestTab={setActiveTab}
        />
      )}
      {tourCompleteOpen && <GuidedTourCompleteDialog busy={tourCompleteBusy} onKeep={finishTourKeepProject} onDelete={() => void finishTourDeleteProject()} />}
      {storageOpen && <StorageRecoveryDialog project={project} snapshots={recoverySnapshots} snapshotsLoading={recoveryLoading} status={storageStatus} persistenceNotice={persistenceNotice} damagedProjects={damagedProjects} busy={dialogBusy || recoveryLoading} onClose={closeStorage} onCreateSnapshot={createSnapshot} onRestore={restoreSnapshot} onDelete={removeSnapshot} onExport={exportRecoverySnapshot} onDownloadDamaged={exportDamagedProject} onDeleteDamaged={deleteDamagedProject} onRequestPersistence={protectStorage} />}
      {compatibilityOpen && <CompatibilityDialog report={compatibility} onClose={() => setCompatibilityOpen(false)} />}
      <PwaUpdateNotice available={pwa.updateAvailable} onUpdate={() => void updateDesktopApp()} />
      {toast && <div className={`toast ${toast.tone}`}>{toast.message}</div>}
    </div>
  )
}

export default App
